import {announce} from '@github-ui/aria-live'
import {useAddBanner, useFocusBanner} from '@github-ui/banner-stack'
import {useMutation} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {Banner, Button, Dialog} from '@primer/react'
import {useEffect, useRef, useState, type RefObject} from 'react'
import {flushSync} from 'react-dom'
import styles from './CachesList.module.css'

const deleteErrorMessage = 'Failed to delete this cache. Please try again.'

type DeleteCacheRequest = {
  deletePath: string
  onClose: () => void
  onDeleted?: () => Promise<void>
}

function useDeleteCache(dialogRef: RefObject<HTMLDivElement | null>, onDialogError: () => void) {
  const addBanner = useAddBanner()
  const focusBanner = useFocusBanner()

  const showBanner = (title: string, variant: 'success' | 'critical', request: DeleteCacheRequest) => {
    const {deletePath} = request
    const id = addBanner({id: `cache-delete-${deletePath}`, title, variant, dismissible: true})
    requestAnimationFrame(() => focusBanner(id))
  }

  return useMutation({
    mutationFn: async ({deletePath}: DeleteCacheRequest) => {
      const response = await reactFetchJSON(deletePath, {
        method: 'DELETE',
        redirect: 'error',
      })
      if (!response.ok) throw new Error(deleteErrorMessage)
      const data: unknown = await response.json()
      if (
        typeof data !== 'object' ||
        data === null ||
        !('message' in data) ||
        typeof data.message !== 'string' ||
        !data.message.trim()
      ) {
        throw new Error(deleteErrorMessage)
      }
      return data.message
    },
    onSuccess: async (message, request) => {
      // eslint-disable-next-line @eslint-react/dom-no-flush-sync -- Finish modal focus restoration before focusing the banner.
      flushSync(request.onClose)
      await request.onDeleted?.()
      showBanner(message, 'success', request)
    },
    onError: (_error, request) => {
      if (dialogRef.current) onDialogError()
      else showBanner(deleteErrorMessage, 'critical', request)
    },
    retry: false,
  })
}

export function CachesDeleteDialog({
  cacheKey,
  deletePath,
  isOpen,
  returnFocusRef,
  onClose,
  track,
  onDeleted,
}: {
  cacheKey: string
  deletePath: string
  isOpen: boolean
  returnFocusRef: RefObject<HTMLButtonElement | null>
  onClose: () => void
  track: (action: string) => void
  onDeleted?: () => Promise<void>
}) {
  const closeRef = useRef<HTMLButtonElement>(null)
  const confirmRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const [errorMessage, setErrorMessage] = useState<string>()
  const deleting = useDeleteCache(dialogRef, () => setErrorMessage(deleteErrorMessage))

  useEffect(() => {
    if (!errorMessage) return
    announce(errorMessage, {assertive: true, element: dialogRef.current ?? undefined})
    const animationFrame = requestAnimationFrame(() => confirmRef.current?.focus())
    return () => cancelAnimationFrame(animationFrame)
  }, [errorMessage])

  const close = () => {
    track(deleting.isPending ? 'delete_dismiss' : 'delete_cancel')
    setErrorMessage(undefined)
    onClose()
  }

  if (!isOpen) return null

  return (
    <Dialog
      ref={dialogRef}
      title={<span className={styles.Text}>Remove {cacheKey}</span>}
      onClose={close}
      initialFocusRef={closeRef}
      returnFocusRef={returnFocusRef}
      renderFooter={() => (
        <Dialog.Footer>
          <Button ref={closeRef} onClick={close}>
            {deleting.isPending ? 'Dismiss' : 'Cancel'}
          </Button>
          <Button
            ref={confirmRef}
            variant="danger"
            loading={deleting.isPending}
            loadingAnnouncement="Deleting cache..."
            onClick={() => {
              if (deleting.isPending) return
              setErrorMessage(undefined)
              track('delete_confirm')
              deleting.mutate({deletePath, onClose, onDeleted})
            }}
          >
            {deleting.isPending ? 'Deleting cache...' : 'Yes, permanently delete this cache'}
          </Button>
        </Dialog.Footer>
      )}
    >
      <p>
        Are you sure you want to delete this cache? <strong>This action cannot be undone</strong>.
      </p>
      {deleting.isPending && <p>Deletion is in progress. Dismissing this dialog will not cancel it.</p>}
      {errorMessage && <Banner title={errorMessage} variant="critical" flush />}
    </Dialog>
  )
}
